import { useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LayoutDashboard,
  Megaphone,
  PartyPopper,
  MapPin,
  BarChart3,
  Hotel,
  Package,
  Users,
  ScrollText,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Moon,
  Sun,
  Search,
  ShieldAlert,
  Route,
  FileCheck,
  TrendingUp,
  FileText,
  Filter,
  Bus,
  AlertTriangle,
  History,
  Siren,
  TrafficCone,
  Zap,
  Compass,
  Inbox,
  Network,
} from 'lucide-react';
import AdminLogin from './Login';
import SetPassword from './SetPassword';
import TwoStepSignIn from './TwoStepSignIn';
import CommandPalette from './CommandPalette';
import { areaForPath } from '@/lib/adminAreas';
import { getAdminRequestCounts } from '@/lib/adminApi';

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
}

interface NavItemWithBadge extends NavItem {
  badgeKey?: string;
}

const navItems: NavItemWithBadge[] = [
  { label: 'Overview', href: '/admin', icon: LayoutDashboard },
  { label: 'Requests', href: '/admin/requests', icon: Inbox, badgeKey: 'requests' },
  { label: 'Sponsors', href: '/admin/sponsors', icon: Megaphone },
  { label: 'Festivals', href: '/admin/festivals', icon: PartyPopper },
  { label: 'Explore', href: '/admin/explore', icon: Compass },
  { label: 'Listings', href: '/admin/listings', icon: MapPin },
  { label: 'Dashboard', href: '/admin/dashboard', icon: BarChart3 },
  { label: 'Revenue', href: '/admin/revenue', icon: TrendingUp },
  { label: 'Reports', href: '/admin/reports', icon: FileText },
  { label: 'Filter Analytics', href: '/admin/filter-analytics', icon: Filter },
  { label: 'Live Map', href: '/admin/live-map', icon: MapPin },
  { label: 'Ride History', href: '/admin/ride-history', icon: History },
  { label: 'Charter', href: '/admin/charter', icon: Bus, badgeKey: 'charter' },
  { label: 'Concierge', href: '/admin/concierge', icon: Hotel },
  { label: 'Corridors', href: '/admin/corridors', icon: Route },
  { label: 'Packages', href: '/admin/packages', icon: Package },
  { label: 'Drivers', href: '/admin/drivers', icon: Users },
  { label: 'Passengers', href: '/admin/passengers', icon: Users },
  { label: 'Driver Verification', href: '/admin/verification', icon: FileCheck, badgeKey: 'verification' },
  { label: 'Trust & Safety', href: '/admin/incidents', icon: ShieldAlert, badgeKey: 'incidents' },
  { label: 'Road Hazards', href: '/admin/hazards', icon: AlertTriangle },
  { label: 'Road Status', href: '/admin/road-status', icon: TrafficCone },
  { label: 'Island Alerts', href: '/admin/alerts', icon: Siren },
  { label: 'Utility Outages', href: '/admin/outages', icon: Zap },
  { label: 'Audit Log', href: '/admin/audit', icon: ScrollText },
  { label: 'Team', href: '/admin/team', icon: Network },
  { label: 'Settings', href: '/admin/settings', icon: Settings },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, isAdmin, loading, signOut, needsPasswordSetup, needsTwoStep, canAccess } = useAdminAuth();
  const showIncidentBadge = isAdmin && canAccess('riders');
  const showVerificationBadge = isAdmin && canAccess('drivers');
  const showCharterBadge = isAdmin && canAccess('rides');
  const [location] = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return true;
  });
  const [cmdOpen, setCmdOpen] = useState(false);
  const [incidentBadge, setIncidentBadge] = useState(0);
  const [verificationBadge, setVerificationBadge] = useState(0);
  // Open + bidding_closed charter requests — these need an operator to
  // glance at them (passenger waiting for bids; passenger reviewing
  // bids).
  const [charterBadge, setCharterBadge] = useState(0);
  // Requests needing the viewer: new ones for directors, replies for staff.
  const [requestBadge, setRequestBadge] = useState(0);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Persist preference
    document.cookie = `admin-theme=${darkMode ? 'dark' : 'light'};path=/;max-age=31536000`;
  }, [darkMode]);

  // Keyboard shortcut for command palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Fetch high/critical open incident count for badge
  useEffect(() => {
    if (!showIncidentBadge) return;
    const fetchBadge = async () => {
      try {
        const { data } = await (await import('@/lib/supabase')).supabase.rpc('list_incident_reports_admin', {
          p_limit: 200,
          p_offset: 0,
        });
        const list = data?.reports ?? (Array.isArray(data) ? data : []);
        const count = list.filter((i: any) =>
          ['open', 'under_review'].includes(i.status) &&
          ['high', 'critical'].includes(i.severity)
        ).length;
        setIncidentBadge(count);
      } catch (e) {
        console.warn('[AdminLayout] incident badge fetch failed', e);
      }
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 30000);
    return () => clearInterval(interval);
  }, [showIncidentBadge]);

  // Fetch manual_review document count for verification badge
  useEffect(() => {
    if (!showVerificationBadge) return;
    const fetchVerificationBadge = async () => {
      try {
        const { data } = await (await import('@/lib/supabase')).supabase.rpc('list_driver_documents_admin', {
          p_status: 'manual_review',
          p_limit: 200,
          p_offset: 0,
        });
        const docs = data?.documents ?? (Array.isArray(data) ? data : []);
        setVerificationBadge(docs.length);
      } catch (e) {
        console.warn('[AdminLayout] verification badge fetch failed', e);
      }
    };
    fetchVerificationBadge();
    const interval = setInterval(fetchVerificationBadge, 30000);
    return () => clearInterval(interval);
  }, [showVerificationBadge]);

  // Charter live activity badge — open + bidding_closed.
  useEffect(() => {
    if (!showCharterBadge) return;
    const fetchCharterBadge = async () => {
      try {
        const { data } = await (await import('@/lib/supabase')).supabase.rpc(
          'admin_list_charter_requests',
          { p_status_filter: null, p_lookback_days: 7 },
        );
        const kpis = (data as any)?.kpis;
        if (kpis) {
          setCharterBadge((kpis.open ?? 0) + (kpis.bidding_closed ?? 0));
        }
      } catch (e) {
        console.warn('[AdminLayout] charter badge fetch failed', e);
      }
    };
    fetchCharterBadge();
    const interval = setInterval(fetchCharterBadge, 30000);
    return () => clearInterval(interval);
  }, [showCharterBadge]);

  useEffect(() => {
    if (!isAdmin) return;
    const fetchRequestBadge = async () => {
      try {
        const counts = await getAdminRequestCounts();
        setRequestBadge(counts.needsAttention);
      } catch (e) {
        console.warn('[AdminLayout] request badge fetch failed', e);
      }
    };
    fetchRequestBadge();
    const interval = setInterval(fetchRequestBadge, 30000);
    window.addEventListener('admin-requests-changed', fetchRequestBadge);
    return () => {
      clearInterval(interval);
      window.removeEventListener('admin-requests-changed', fetchRequestBadge);
    };
  }, [isAdmin]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#070b13] flex items-center justify-center">
        <div className="flex flex-col items-center gap-5">
          <img
            src="/logo-transparent.png"
            alt="H-Plate"
            className="w-12 h-12 object-contain animate-float logo-glow"
          />
          <div className="space-y-3 w-64">
            <Skeleton className="h-8 w-48 bg-gray-800/60" />
            <Skeleton className="h-4 w-32 bg-gray-800/60" />
            <Skeleton className="h-4 w-40 bg-gray-800/60" />
          </div>
        </div>
      </div>
    );
  }

  if (user && needsPasswordSetup) {
    return <SetPassword />;
  }

  if (user && needsTwoStep) {
    return <TwoStepSignIn />;
  }

  // Not authenticated or not admin
  if (!user || !isAdmin) {
    return <AdminLogin />;
  }

  const initials = user.email?.slice(0, 2).toUpperCase() || 'AD';
  const visibleNav = navItems.filter((item) => canAccess(areaForPath(item.href)));
  const pageAllowed = canAccess(areaForPath(location));
  const blockedPageLabel = navItems.find((item) => item.href !== '/admin' && location.startsWith(item.href))?.label ?? location;

  return (
    <div className="min-h-screen text-gray-100 flex" style={{ background: '#070b13' }}>
      {/* Subtle ambient brand glow in the top-left corner of the canvas */}
      <div
        className="pointer-events-none fixed top-0 left-0 w-[36rem] h-[36rem] -translate-x-1/3 -translate-y-1/3 rounded-full opacity-25 blur-[120px]"
        style={{
          background:
            'radial-gradient(closest-side, #06b6d4 0%, #0891b2 35%, transparent 75%)',
        }}
      />
      <div
        className="pointer-events-none fixed bottom-0 right-0 w-[40rem] h-[40rem] translate-x-1/3 translate-y-1/3 rounded-full opacity-15 blur-[140px]"
        style={{
          background:
            'radial-gradient(closest-side, #10b981 0%, #064e3b 40%, transparent 80%)',
        }}
      />

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 flex flex-col border-r border-white/5 transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
        style={{
          background:
            'linear-gradient(180deg, rgba(17,24,39,0.92) 0%, rgba(11,15,23,0.92) 100%)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-white/5">
          {!collapsed ? (
            <Link href="/admin" className="flex items-center gap-2.5 no-underline group">
              <img
                src="/logo-transparent.png"
                alt="H-Plate"
                className="w-8 h-8 object-contain logo-glow"
              />
              <div className="flex flex-col leading-tight">
                <span className="font-display text-base text-white tracking-tight group-hover:text-cyan-300 transition-colors">
                  H-Plate
                </span>
                <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-cyan-400/70">
                  Control Room
                </span>
              </div>
            </Link>
          ) : (
            <Link href="/admin" className="mx-auto no-underline">
              <img
                src="/logo-transparent.png"
                alt="H-Plate"
                className="w-8 h-8 object-contain logo-glow"
              />
            </Link>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {visibleNav.map((item) => {
            const isActive = location === item.href || (item.href !== '/admin' && location.startsWith(item.href));
            const Icon = item.icon;
            return (
              <Link key={item.href} href={item.href}>
                <div
                  className={`relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                    isActive
                      ? 'text-white font-medium'
                      : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                  }`}
                  style={
                    isActive
                      ? {
                          background:
                            'linear-gradient(90deg, rgba(6,182,212,0.18) 0%, rgba(16,185,129,0.10) 100%)',
                          boxShadow: 'inset 0 0 0 1px rgba(6,182,212,0.25)',
                        }
                      : undefined
                  }
                >
                  {/* Active indicator strip */}
                  {isActive && (
                    <span
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 rounded-full"
                      style={{
                        background:
                          'linear-gradient(180deg, #67e8f9 0%, #06b6d4 100%)',
                      }}
                    />
                  )}
                  <Icon
                    className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-300' : ''}`}
                  />
                  {!collapsed && (
                    <>
                      <span className="flex-1">{item.label}</span>
                      {item.badgeKey === 'incidents' && incidentBadge > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-rose-500 text-white rounded-full min-w-[18px] text-center shadow-[0_0_12px_rgba(244,63,94,0.4)]">
                          {incidentBadge}
                        </span>
                      )}
                      {item.badgeKey === 'verification' && verificationBadge > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full min-w-[18px] text-center shadow-[0_0_12px_rgba(245,158,11,0.4)]">
                          {verificationBadge}
                        </span>
                      )}
                      {item.badgeKey === 'charter' && charterBadge > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-emerald-500 text-white rounded-full min-w-[18px] text-center shadow-[0_0_12px_rgba(16,185,129,0.4)]">
                          {charterBadge}
                        </span>
                      )}
                      {item.badgeKey === 'requests' && requestBadge > 0 && (
                        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold bg-cyan-500 text-white rounded-full min-w-[18px] text-center shadow-[0_0_12px_rgba(6,182,212,0.4)]">
                          {requestBadge}
                        </span>
                      )}
                    </>
                  )}
                  {collapsed && item.badgeKey === 'requests' && requestBadge > 0 && (
                    <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-cyan-500 rounded-full" />
                  )}
                  {collapsed && item.badgeKey === 'incidents' && incidentBadge > 0 && (
                    <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-rose-500 rounded-full live-dot" />
                  )}
                  {collapsed && item.badgeKey === 'verification' && verificationBadge > 0 && (
                    <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-amber-500 rounded-full" />
                  )}
                  {collapsed && item.badgeKey === 'charter' && charterBadge > 0 && (
                    <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-emerald-500 rounded-full live-dot" />
                  )}
                </div>
              </Link>
            );
          })}
        </nav>

        {/* Collapse toggle */}
        <div className="p-2 border-t border-white/5">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center text-gray-400 hover:text-cyan-300 hover:bg-white/[0.04]"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className={`relative flex-1 flex flex-col transition-all duration-200 ${collapsed ? 'ml-16' : 'ml-60'}`}>
        {/* Top bar */}
        <header
          className="sticky top-0 z-20 h-14 border-b border-white/5 flex items-center justify-between px-6"
          style={{
            background: 'rgba(7,11,19,0.75)',
            backdropFilter: 'blur(14px) saturate(140%)',
            WebkitBackdropFilter: 'blur(14px) saturate(140%)',
          }}
        >
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Admin</span>
            <span className="text-gray-700">/</span>
            <span
              className="font-medium capitalize"
              style={{
                background: 'linear-gradient(90deg, #67e8f9 0%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {location === '/admin' ? 'Overview' : location.split('/').pop()?.replace(/-/g, ' ')}
            </span>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Live system pulse */}
            <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
                Live
              </span>
            </div>

            {/* Command palette trigger */}
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-cyan-300 hover:bg-white/[0.04] gap-2 hidden sm:flex"
              onClick={() => setCmdOpen(true)}
            >
              <Search className="w-3.5 h-3.5" />
              <span className="text-xs">Search</span>
              <kbd className="ml-1 px-1.5 py-0.5 text-[10px] bg-white/[0.06] rounded border border-white/10 text-gray-400">
                ⌘K
              </kbd>
            </Button>

            {/* Dark mode toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-400 hover:text-cyan-300 hover:bg-white/[0.04] w-8 h-8"
              onClick={() => setDarkMode(!darkMode)}
            >
              {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            <Separator orientation="vertical" className="h-6 bg-white/10" />

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 text-gray-300 hover:text-white hover:bg-white/[0.04]">
                  <Avatar className="w-6 h-6">
                    <AvatarFallback className="bg-cyan-500/15 text-cyan-300 text-xs border border-cyan-500/25">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  {!collapsed && <span className="text-xs hidden md:inline">{user.email}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-48 border-white/10"
                style={{
                  background: 'rgba(17,24,39,0.95)',
                  backdropFilter: 'blur(14px)',
                  WebkitBackdropFilter: 'blur(14px)',
                }}
              >
                {canAccess('audit') && (
                  <>
                    <DropdownMenuItem className="text-gray-300 focus:bg-white/[0.06] focus:text-cyan-300 cursor-pointer" asChild>
                      <Link href="/admin/audit">
                        <ScrollText className="w-3.5 h-3.5 mr-2" />
                        View audit log
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                  </>
                )}
                <DropdownMenuItem
                  className="text-rose-400 focus:bg-rose-500/10 focus:text-rose-300 cursor-pointer"
                  onClick={() => signOut()}
                >
                  <LogOut className="w-3.5 h-3.5 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="relative flex-1 p-6 overflow-auto">
          {pageAllowed ? children : (
            <div className="max-w-md mx-auto mt-24 text-center space-y-3">
              <ShieldAlert className="w-8 h-8 text-gray-500 mx-auto" />
              <h1 className="text-lg font-semibold text-white">This page is not part of your access</h1>
              <p className="text-sm text-gray-400">
                If you need it for your work, ask a director. They can change your access.
              </p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <Link href={`/admin/requests?new=1&kind=access&subject=${encodeURIComponent(`Access to ${blockedPageLabel}`)}&path=${encodeURIComponent(location)}`}>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white">Ask for access</Button>
                </Link>
                <Link href="/admin">
                  <Button variant="outline" size="sm" className="border-gray-700 text-gray-300">Back to Overview</Button>
                </Link>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Command palette */}
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
    </div>
  );
}
